import { NavLink, Link, useLocation } from 'react-router-dom'
import {
  Activity,
  Crosshair,
  LayoutDashboard,
  Swords,
  Users,
} from 'lucide-react'
import { cn } from '@/lib/utils'

const links = [
  { to: '/', label: 'Home', icon: LayoutDashboard, end: true },
  { to: '/missions', label: 'Missions', icon: Crosshair },
  { to: '/players', label: 'Players', icon: Users },
  { to: '/weapons', label: 'Weapons', icon: Swords },
  { to: '/compare', label: 'Compare', icon: Activity },
]

export function Shell({ children }: { children: React.ReactNode }) {
  const location = useLocation()

  return (
    <div className="min-h-screen flex flex-col pb-20 md:pb-0">
      <header className="sticky top-0 z-40 border-b border-line/80 bg-canvas/80 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 h-16 flex items-center justify-between gap-6">
          <Link to="/" className="group flex items-center gap-3 shrink-0">
            <span className="relative flex h-9 w-9 items-center justify-center rounded-control border border-line bg-canvas-raised font-mono text-sm font-semibold text-accent transition-colors group-hover:border-accent/40">
              SL
              <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-accent" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-display text-[15px] font-semibold tracking-tight text-ink">
                StatsLogger
              </span>
              <span className="mt-1 text-[10px] uppercase tracking-[0.18em] text-ink-faint">
                Arma 3 analytics
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-0.5 rounded-panel border border-line bg-canvas-raised/60 p-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  cn(
                    'relative rounded-control px-3.5 py-1.5 text-sm font-medium transition-all duration-200',
                    isActive
                      ? 'bg-canvas-overlay text-accent-text shadow-sm'
                      : 'text-ink-muted hover:text-ink',
                  )
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:block w-[72px]" aria-hidden />
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-7xl px-4 py-8">{children}</main>

      <footer className="hidden md:block border-t border-line/60 py-5 text-center text-xs text-ink-faint">
        StatsLogger · mission intelligence
      </footer>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 border-t border-line bg-canvas/95 backdrop-blur-xl safe-bottom">
        <div className="grid grid-cols-5 h-16">
          {links.map((l) => {
            const Icon = l.icon
            const active = l.end
              ? location.pathname === l.to
              : location.pathname === l.to || location.pathname.startsWith(l.to + '/')
            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={cn(
                  'flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors',
                  active ? 'text-accent-text' : 'text-ink-faint',
                )}
              >
                <Icon size={18} strokeWidth={active ? 2.25 : 1.75} />
                {l.label}
              </NavLink>
            )
          })}
        </div>
      </nav>
    </div>
  )
}
